import React, { useLayoutEffect, useRef, useState, useMemo, useEffect } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5core from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import './Contextualizer.scss';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { RGBColor } from '../../models/Card'; // Imported RGBColor
import {
  updateSelectedCards,
  clearSelectedCards,
  removeSelectedCards,
} from '../../features/rod-lift-analysis/CardDateSlice';
import { addSelectedRange, clearSelectedRanges, setSelectedRanges } from './selectedRangesSlice';
import ContextualizerLegends from './ContextualizerLegends';
import { LegendItemType } from '../../components/card-viewer/CardViewer';
import Loader from '../common/Loader/Loader';
import { showWarningToast } from '../../components/toast/toastBridge';
import { ALLY_CONFIGURATIONS } from '../../services/configuration/config';
import closeSVG from './../../images/close-circle.svg';
// Enum for analytics types
export enum AnalyticsType {
  UpliftOpportunity = 'upliftOpportunity',
  RodsOverloaded = 'rodsOverloaded',
  PumpTagging = 'pumpTagging',
  GearboxOverloaded = 'gearboxOverloaded',
  StandingValveLeak = 'standingValveLeak',
}
// Prepare data
// Define a local type for analytics filtering
interface CardDateItemWithAnalytics {
  date: string;
  isSelected: boolean;
  cardTypeName: string;
  color: RGBColor;
  upliftOpportunity?: boolean;
  rodsOverloaded?: boolean;
  pumpTagging?: boolean;
  gearboxOverloaded?: boolean;
  standingValveLeak?: boolean;
}

const loadSvgAsDataUri = (url: string, signal?: AbortSignal): Promise<string> => {
  return fetch(url, { signal })
    .then((res) => res.text())
    .then((svg) => `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`);
};

const Contextualizer = (props: {
  cardDateItems: {
    date: string;
    isSelected: boolean;
    cardTypeName: string;
    color: RGBColor;
  }[];
  fetchCardDataFormMultipleSelectedDates: (dates: string[]) => void;
  isCardAnalytics?: boolean;
  legendsData?: LegendItemType[];
  chartTitle: string;
  addAmChartRef?: (index: number, chart: am5xy.XYChart) => void;
  chartIndex?: number;
  syncAxes?: (chart: am5xy.XYChart) => void;
  isStreamingComplete: boolean;
}) => {
  const [iconUri, setIconUri] = useState<string>();
  const dispatch = useAppDispatch();
  const chartRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<am5.Root | null>(null);
  const isLoadingCards = useAppSelector((state) => state.rodLiftAnalysis.isLoading);
  const isDraggingRef = useRef(false);
  // Get startDate and endDate from Redux state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const startDate = useAppSelector((state: any) => state.AssetTrendDataSlice.startDate);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const endDate = useAppSelector((state: any) => state.AssetTrendDataSlice.endDate);

  const {
    cardDateItems,
    fetchCardDataFormMultipleSelectedDates,
    isCardAnalytics,
    legendsData,
    chartTitle,
    addAmChartRef,
    chartIndex,
    syncAxes,
    isStreamingComplete,
  } = props;

  // Define color mapping for card types
  const cardTypeColors = {
    Current: '#6bbbae',
    Shutdown: '#F04438',
    Startup: '#4294FF',
    'Pump off': '#FFBF3F',
  };
  // TESTING: Randomly update datapoints for analytics
  // const testCardDateItems = cardDateItems.map((item, idx) => {
  //   if (idx === 1)
  //     return {
  //       ...item,
  //       upliftOpportunity: 0,
  //       rodsOverloaded: 1,
  //       pumpTagging: 0,
  //       gearboxOverloaded: 1,
  //       standingValveLeak: 0,
  //     };
  //   if (idx === 2) return { ...item, pumpTagging: 1 };
  //   if (idx === 3) return { ...item, gearboxOverloaded: 1 };
  //   if (idx === 4) return { ...item, standingValveLeak: 1 };
  //   return item;
  // }) as CardDateItemWithAnalytics[];

  const filteredCardDateItems = cardDateItems as CardDateItemWithAnalytics[];
  //const [isResizing, setIsResizing] = useState(false);
  const isResizingRef = useRef(false);

  // Create stable reference for chart data based on core data properties only
  const chartData = useMemo(() => {
    let data: Array<{ date: number; value: number; cardType: string; color: string; analyticsType?: AnalyticsType }>;
    if (isCardAnalytics) {
      data = filteredCardDateItems.reduce(
        (acc, item) => {
          let isDataPointAdded = false;
          if (item.upliftOpportunity) {
            acc.push({
              date: new Date(item.date).getTime(),
              value: 16,
              cardType: item.cardTypeName,
              color: '#12B76A',
              analyticsType: AnalyticsType.UpliftOpportunity,
            });
            isDataPointAdded = true;
          }
          if (item.rodsOverloaded) {
            acc.push({
              date: new Date(item.date).getTime(),
              value: 16,
              cardType: item.cardTypeName,
              color: '#FECDCA',
              analyticsType: AnalyticsType.RodsOverloaded,
            });
            isDataPointAdded = true;
          }
          if (item.pumpTagging) {
            acc.push({
              date: new Date(item.date).getTime(),
              value: 16,
              cardType: item.cardTypeName,
              color: '#F97066',
              analyticsType: AnalyticsType.PumpTagging,
            });
            isDataPointAdded = true;
          }
          if (item.gearboxOverloaded) {
            acc.push({
              date: new Date(item.date).getTime(),
              value: 16,
              cardType: item.cardTypeName,
              color: '#F8AA62',
              analyticsType: AnalyticsType.GearboxOverloaded,
            });
            isDataPointAdded = true;
          }
          if (item.standingValveLeak) {
            acc.push({
              date: new Date(item.date).getTime(),
              value: 16,
              cardType: item.cardTypeName,
              color: '#C471F3',
              analyticsType: AnalyticsType.StandingValveLeak,
            });
            isDataPointAdded = true;
          }
          if (!isDataPointAdded) {
            acc.push({
              date: new Date(item.date).getTime(),
              value: 0,
              cardType: item.cardTypeName,
              color: '#ffffff ',
              analyticsType: AnalyticsType.StandingValveLeak,
            });
          }
          return acc;
        },
        [] as Array<{ date: number; value: number; cardType: string; color: string; analyticsType: AnalyticsType }>,
      );
    } else {
      data = filteredCardDateItems?.map((item) => {
        return {
          date: new Date(item.date).getTime(),
          value: 7,
          cardType: item.cardTypeName,
          color: cardTypeColors[item.cardTypeName as keyof typeof cardTypeColors] || cardTypeColors.Current,
        };
      });
    }
    return data;
  }, [
    // Dependency on original cardDateItems to ensure all contextualizer instances re-render when any instance changes
    JSON.stringify(
      cardDateItems?.map((item) => ({
        date: item.date,
        cardTypeName: item.cardTypeName,
        // Include analytics proper ties for analytics charts
        ...(isCardAnalytics
          ? {
              upliftOpportunity: (item as CardDateItemWithAnalytics).upliftOpportunity,
              rodsOverloaded: (item as CardDateItemWithAnalytics).rodsOverloaded,
              pumpTagging: (item as CardDateItemWithAnalytics).pumpTagging,
              gearboxOverloaded: (item as CardDateItemWithAnalytics).gearboxOverloaded,
              standingValveLeak: (item as CardDateItemWithAnalytics).standingValveLeak,
            }
          : {}),
      })),
    ),
    isCardAnalytics,
  ]);

  const selectedCards = useAppSelector((state) => state.rodLiftAnalysis.selectedCardDates);
  const selectedRanges = useAppSelector((state) => state.selectedRanges.selectedRanges);

  // Store chart instance in ref to access from selection effect
  const chartInstanceRef = useRef<am5xy.XYChart | null>(null);
  const xAxisRef = useRef<am5xy.DateAxis<am5xy.AxisRenderer> | null>(null);
  // State to trigger selection redraw after resize
  const [resizeVersion, setResizeVersion] = useState(0);

  useLayoutEffect(() => {
    if (!chartRef.current) return;

    // Wait for chartData to be available
    if (!chartData || chartData.length === 0) return;

    // Declare variables at the top of the effect scope
    let isDragging = false;
    isDraggingRef.current = false;
    let dragStartDate: number | null = null;
    let dragEndDate: number | null = null;
    // let preservedZoomState: { start: number; end: number } | null = null;
    // let recentDragOperation = false;

    // Create root element
    const root = am5.Root.new(chartRef.current);
    rootRef.current = root;

    // Set themes
    root.setThemes([am5themes_Dark.new(root)]);

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        paddingLeft: 10,
        paddingRight: 30,
        paddingTop: 0,
        paddingBottom: 0,
      }),
    );

    type SyncableChart = am5xy.XYChart & {
      _isReceiverOnly?: boolean;
      _isDragging?: boolean;
      _recentDragOperation?: boolean;
      _allowZoomSync?: boolean;
      _isReceivingSync?: boolean;
      _zoomMonitorInterval?: number;
      _selectionRects?: Set<am5.Rectangle>;
    };
    const syncChart = chart as unknown as SyncableChart;
    syncChart._selectionRects = new Set<am5.Rectangle>();

    // Store chart reference for selection effect
    chartInstanceRef.current = chart;

    // Set chart height
    // chart.set("height", 88);

    // Create axes
    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxDeviation: 0.2,
        baseInterval: {
          timeUnit: 'second',
          count: 1,
        },
        renderer: am5xy.AxisRendererX.new(root, {
          strokeWidth: 1,
          strokeOpacity: 1,
          stroke: am5core.color('#4A5463'),
          minGridDistance: 80,
          minorGridEnabled: true,
        }),
        tooltip: am5.Tooltip.new(root, {}),
      }),
    );

    // Store xAxis reference for selection effect
    xAxisRef.current = xAxis;

    if (startDate && endDate) {
      xAxis.setAll({
        min: new Date(startDate).getTime(),
        max: new Date(endDate).getTime(),
        strictMinMax: true,
      });
    }

    const yAxisRenderer = am5xy.AxisRendererY.new(root, {
      minorGridEnabled: true,
      strokeOpacity: 1,
      visible: true,
      inside: false,
    });

    const xAxisRenderer = xAxis.get('renderer');

    xAxisRenderer.grid.template.setAll({
      strokeOpacity: 0,
    });

    xAxisRenderer.labels.template.setAll({
      fontFamily: 'Mulish',
      fontSize: '12px',
      fontWeight: '400',
      paddingTop: 10,
      minPosition: 0,
      maxPosition: 1,
      location: 0,
    });

    xAxisRenderer.ticks.template.setAll({
      stroke: am5core.color('#FFFFFF'),
      strokeOpacity: 1,
      visible: true,
      minPosition: 0,
      maxPosition: 1,
    });
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: yAxisRenderer,
        min: 0, // Set minimum value for y-axis
        max: isCardAnalytics ? 100 : 11, // Set maximum value for y-axis (adjust as needed)
      }),
    );

    // Create series
    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Series 1',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'value',
        valueXField: 'date',
        // Disable data grouping to ensure individual points render when zoomed very tightly
        groupDataDisabled: true,
        tooltip: am5.Tooltip.new(root, {
          labelText: '{valueY}',
        }),
      }),
    );

    // Style the columns with dynamic colors based on card type or analytics
    series.columns.template.setAll({
      width: 2,
      cornerRadiusTL: 0,
      cornerRadiusTR: 0,
    });
    // Hide columns with value 0
    series.columns.template.adapters.add('visible', (_visible, target) => {
      const dataItem = (target as unknown as { dataItem?: { dataContext?: { value?: number } } }).dataItem;
      if (dataItem && dataItem.dataContext && dataItem.dataContext.value === 0) {
        return false;
      }
      return true;
    });

    // Center columns within their time cell to avoid edge clipping
    //series.set('locationX', 0.5);
    // Update the adapters for fill and stroke to use fixed values instead of data or selectedCards
    series.columns.template.adapters.add('fill', (fill, target) => {
      // target is RoundedRectangle, but dataItem is not typed, so use unknown and cast
      const dataItem = (
        target as unknown as { dataItem?: { dataContext?: { color?: string }; get?: (key: string) => unknown } }
      ).dataItem;
      if (dataItem) {
        if (dataItem.dataContext && typeof dataItem.dataContext.color === 'string') {
          const color = am5.color(dataItem.dataContext.color);
          return color;
        }
        const cardType = dataItem.get && (dataItem.get('categoryX') as keyof typeof cardTypeColors | undefined);
        if (cardType && Object.prototype.hasOwnProperty.call(cardTypeColors, cardType)) {
          return am5.color(cardTypeColors[cardType]);
        }
        return am5.color(cardTypeColors.Current);
      }
      return fill;
    });

    series.columns.template.adapters.add('stroke', (stroke, target) => {
      const dataItem = (
        target as unknown as { dataItem?: { dataContext?: { color?: string }; get?: (key: string) => unknown } }
      ).dataItem;
      if (dataItem) {
        // Use color from dataContext if present
        if (dataItem.dataContext && typeof dataItem.dataContext.color === 'string') {
          return am5.color(dataItem.dataContext.color);
        }
        const cardType = dataItem.get && (dataItem.get('categoryX') as keyof typeof cardTypeColors | undefined);
        if (cardType && Object.prototype.hasOwnProperty.call(cardTypeColors, cardType)) {
          return am5.color(cardTypeColors[cardType]);
        }
        return am5.color(cardTypeColors.Current);
      }
      return stroke;
    });

    series.columns.template.adapters.add('y', (y, target) => {
      const dataItem = (
        target as unknown as {
          dataItem?: {
            dataContext?: { color?: string; analyticsType?: AnalyticsType };
            get?: (key: string) => unknown;
          };
        }
      ).dataItem;
      if (dataItem) {
        if (isCardAnalytics && dataItem.dataContext) {
          if (dataItem.dataContext.analyticsType === AnalyticsType.UpliftOpportunity) {
            return 80;
          }
          if (dataItem.dataContext.analyticsType === AnalyticsType.RodsOverloaded) {
            return 60;
          }
          if (dataItem.dataContext.analyticsType === AnalyticsType.PumpTagging) {
            return 40;
          }
          if (dataItem.dataContext.analyticsType === AnalyticsType.GearboxOverloaded) {
            return 20;
          }
          if (dataItem.dataContext.analyticsType === AnalyticsType.StandingValveLeak) {
            return 0;
          }
        } else {
          const cardType = dataItem.get && (dataItem.get('categoryX') as keyof typeof cardTypeColors);
          if (cardType != 'Current') {
            return 22; // Start y position for tertiary card types
          }
        }
      }
      return y;
    });

    // Style the chart background
    chart.plotContainer.set(
      'background',
      am5.Rectangle.new(root, {
        fill: am5.color('#253040'),
      }),
    );

    // Style axes with visible lines
    xAxis.get('renderer').set('stroke', am5.color('#888888'));
    xAxis.get('renderer').set('strokeWidth', 1);
    xAxis.get('renderer').set('strokeOpacity', 1);
    xAxis.get('renderer').labels.template.setAll({
      fill: am5.color('#f7f9f9'),
      fontSize: 12,
      fontFamily: 'Mulish',
    });
    yAxis.get('renderer').set('stroke', am5.color('#ffffff'));
    yAxis.get('renderer').labels.template.setAll({
      visible: false, // Hide Y-axis labels
    });

    // Create a custom Y-axis line using Rectangle since Line might not work as expected
    chart.plotContainer.children.push(
      am5.Rectangle.new(root, {
        fill: am5.color('#888888'),
        width: 0.5,
        height: am5.percent(100),
        x: 0,
        y: 0,
        layer: 10, // Put it on a high layer to ensure visibility
      }),
    );

    // Correct tooltip and cursor assignments
    series.set('tooltip', undefined);
    xAxis.set('tooltip', undefined);

    // Sort data by date to ensure DateAxis receives ordered points
    const sortedData = [...chartData].sort((a, b) => a.date - b.date);

    // Dynamically set baseInterval to the smallest gap found, so columns calculate positions correctly when zoomed
    const computeBaseInterval = () => {
      if (sortedData.length < 2) return { timeUnit: 'second', count: 1 } as const;
      let minDiff = Number.POSITIVE_INFINITY;
      for (let i = 1; i < sortedData.length; i++) {
        const diff = sortedData[i].date - sortedData[i - 1].date;
        if (diff > 0 && diff < minDiff) minDiff = diff;
      }
      if (!isFinite(minDiff) || minDiff <= 0) return { timeUnit: 'second', count: 1 } as const;
      const second = 1000;
      const minute = 60 * second;
      const hour = 60 * minute;
      if (minDiff >= hour) {
        return { timeUnit: 'hour', count: Math.max(1, Math.round(minDiff / hour)) } as const;
      }
      if (minDiff >= minute) {
        return { timeUnit: 'minute', count: Math.max(1, Math.round(minDiff / minute)) } as const;
      }
      return { timeUnit: 'second', count: Math.max(1, Math.round(minDiff / second)) } as const;
    };

    try {
      const bi = computeBaseInterval();
      xAxis.set('baseInterval', bi);
    } catch {
      // safe-guard: keep existing baseInterval
    }

    // Set data
    series.data.setAll(sortedData);
    // DateAxis does not need explicit data; removing prevents any unintended coupling

    // Enable click to zoom on the chart
    chart.set('wheelX', 'none');
    chart.set('wheelY', 'none');

    // Add zooming behavior
    const cursor = chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        behavior: 'none', // enable built-in zoom again
      }),
    );

    cursor.lineX.setAll({ visible: false });
    cursor.lineY.setAll({ visible: false });

    xAxis.get('renderer').grid.template.set('visible', false);
    yAxis.get('renderer').grid.template.set('visible', false);

    // Enable our own drag-to-zoom using the drag selection rectangle; this avoids amCharts changing axis start/end in ways
    // that can cull narrow columns while still within the range.
    // Add a background range for drag preview
    const backgroundRange = chart.plotContainer.children.push(
      am5.Rectangle.new(root, {
        fill: am5.color('#C3E7F1'), // Use same color as selection rectangles for visibility
        fillOpacity: 0.3, // Slightly more transparent to distinguish from final selection
        width: 0, // Start with no width
        height: am5.percent(100),
        layer: 1, // Put on layer 1 to be visible during drag (above selection rectangles on layer 0)
        visible: false, // Start hidden
      }),
    );
    // Background range updates will be handled by separate useEffect for selectedRanges

    // Add resize listeners to update background range and set isResizing
    // Use a generic approach that works across screen resolutions and OSes:
    // - ResizeObserver for container size changes
    // - window/visualViewport resize for viewport changes
    let resizeTimeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      isResizingRef.current = true;
      // Clear previous selected range rectangles safely - use custom property for reliable identification
      const toRemove: am5.Rectangle[] = [];
      const closeIconsToRemove: am5.Sprite[] = [];
      const rectContainersToRemove: am5.Sprite[] = [];
      chart.plotContainer.children.each((child) => {
        if (child && child instanceof am5.Rectangle && syncChart._selectionRects?.has(child)) {
          toRemove.push(child);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } else if (child && child instanceof am5.Picture && (child as any).isCloseIcon) {
          closeIconsToRemove.push(child);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } else if (child && child instanceof am5.Container && (child as any).isRectContainer) {
          rectContainersToRemove.push(child);
        }
      });
      toRemove.forEach((rect) => rect.dispose());
      syncChart._selectionRects?.clear();
      closeIconsToRemove.forEach((icon) => icon.dispose());
      rectContainersToRemove.forEach((container) => container.dispose());

      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        isResizingRef.current = false;

        // Trigger selection redraw by updating resize version
        setResizeVersion((prev) => prev + 1);
      }, 100); // Reduced from 500ms to 100ms for faster response
    };
    // Observe the chart container for size changes (works when panels/layout change without window resize)
    let resizeObserver: ResizeObserver | null = null;
    if (chartRef.current && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => {
        // Use rAF to ensure layout has settled before recompute
        requestAnimationFrame(() => handleResize());
      });
      resizeObserver.observe(chartRef.current);
    }
    // Fallback to window resize; also listen to visualViewport where available (handles zoom/DPI changes better)
    window.addEventListener('resize', handleResize);
    const vv = (window as unknown as { visualViewport?: VisualViewport }).visualViewport;
    const onVisualViewportResize = () => handleResize();
    if (vv && typeof vv.addEventListener === 'function') {
      vv.addEventListener('resize', onVisualViewportResize);
    }

    chart.plotContainer.events.on('pointerdown', (event) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((event.target as any).isCloseIcon) {
        return;
      }
      const point = chart.plotContainer.toLocal(event.point);
      const start = xAxis.get('start') ?? 0;
      const end = xAxis.get('end') ?? 1;
      const relPlotX = chart.plotContainer.width() ? point.x / chart.plotContainer.width() : 0;
      const relPos = Math.max(0, Math.min(1, start + relPlotX * (end - start)));
      const date = xAxis.positionToValue(relPos);
      dragStartDate = date;
      isDragging = true;
      isDraggingRef.current = true;
    });

    chart.plotContainer.events.on('globalpointermove', (event) => {
      if (!isDragging) return;

      const point = chart.plotContainer.toLocal(event.point);
      const start = xAxis.get('start') ?? 0;
      const end = xAxis.get('end') ?? 1;
      const relPlotX = chart.plotContainer.width() ? point.x / chart.plotContainer.width() : 0;
      const relPos = Math.max(0, Math.min(1, start + relPlotX * (end - start)));
      const date = xAxis.positionToValue(relPos);

      if (dragStartDate !== null) {
        const selectedDates = [dragStartDate, date].sort((a, b) => a - b);
        const minDate = selectedDates[0];
        const maxDate = selectedDates[1];

        // Calculate positions while preserving zoom
        const minPosition = xAxis.valueToPosition(minDate);
        const maxPosition = xAxis.valueToPosition(maxDate);
        const start = xAxis.get('start') ?? 0;
        const end = xAxis.get('end') ?? 1;
        const axisSpan = end - start;
        const safeSpan = axisSpan !== 0 ? axisSpan : 1;
        const xMin = ((minPosition - start) / safeSpan) * chart.plotContainer.width();
        const xMax = ((maxPosition - start) / safeSpan) * chart.plotContainer.width();

        backgroundRange.setAll({
          x: xMin,
          width: Math.max(0, xMax - xMin),
          visible: true,
        });
      }
    });

    chart.plotContainer.events.on('pointerup', (event) => {
      const point = chart.plotContainer.toLocal(event.point);
      const start = xAxis.get('start') ?? 0;
      const end = xAxis.get('end') ?? 1;
      const relPlotX = chart.plotContainer.width() ? point.x / chart.plotContainer.width() : 0;
      const relPos = Math.max(0, Math.min(1, start + relPlotX * (end - start)));
      const date = xAxis.positionToValue(relPos);
      dragEndDate = date;

      let updatedSelectedCards = [];
      if (dragStartDate !== null && dragEndDate !== null && dragStartDate !== dragEndDate) {
        const selectedDates = [dragStartDate, dragEndDate].sort((a, b) => a - b);
        const minDate = selectedDates[0];
        const maxDate = selectedDates[1];
        const updatedRange = { minDate, maxDate };
        // Update globalSelectedDataPoints with data points in the selected range
        const selectedDataDates = cardDateItems
          .filter((dataPoint) => {
            const dataPointTime = new Date(dataPoint.date).getTime();
            return dataPointTime >= minDate && dataPointTime <= maxDate;
          })
          ?.map((dataPoint) => dataPoint.date);

        // Deduplicate to avoid growing selection with duplicates across overlapping ranges
        updatedSelectedCards = Array.from(new Set([...(selectedCards || []), ...(selectedDataDates || [])]));

        if (updatedSelectedCards?.length < ALLY_CONFIGURATIONS.MAX_CARDS_SELECTABLE) {
          dispatch(addSelectedRange(updatedRange));
          const minPos = xAxis.valueToPosition(minDate);
          const maxPos = xAxis.valueToPosition(maxDate);
          const start = xAxis.get('start') ?? 0;
          const end = xAxis.get('end') ?? 1;
          const axisSpan = end - start;
          const safeSpan = axisSpan !== 0 ? axisSpan : 1;
          const xStart = ((minPos - start) / safeSpan) * chart.plotContainer.width();
          const xEnd = ((maxPos - start) / safeSpan) * chart.plotContainer.width();
          const dragSelectionRect = am5.Rectangle.new(root, {
            fill: am5.color('#C3E7F1'),
            fillOpacity: 0.5,
            width: Math.max(0, xEnd - xStart),
            height: am5.percent(100),
            x: xStart,
            layer: 0,
          });
          chart.plotContainer.children.push(dragSelectionRect);
          syncChart._selectionRects?.add(dragSelectionRect);
          dispatch(updateSelectedCards(updatedSelectedCards));
          //if (!isCardAnalytics)
          fetchCardDataFormMultipleSelectedDates(updatedSelectedCards);
        } else {
          backgroundRange.setAll({
            x: 0,
            width: 0,
            visible: false,
          });
          showWarningToast(`You can add only ${ALLY_CONFIGURATIONS.MAX_CARDS_SELECTABLE} cards to the chart.`);
        }
      }

      // Hide background range after operation completes
      backgroundRange.set('visible', false);

      dragStartDate = null;
      dragEndDate = null;
      isDragging = false;
      isDraggingRef.current = false;
    });

  // Hook into AmCharts' native zoom functionality
  // Monitor zoom state to detect ANY zoom changes (in/out or within zoomed range) and clear selections
  let isCurrentlyZoomedIn = false;
  let prevStart = xAxis.get('start') || 0;
  let prevEnd = xAxis.get('end') || 1;

    // Simple polling approach to detect zoom changes
    const monitorZoomState = () => {
      const currentStart = xAxis.get('start') || 0;
      const currentEnd = xAxis.get('end') || 1;
      const isZoomedNow = !(currentStart === 0 && currentEnd === 1);

      // Detect ANY change in visible range (start/end) while not receiving sync
      const rangeChanged = currentStart !== prevStart || currentEnd !== prevEnd;
      if (rangeChanged && !syncChart._isReceivingSync) {
        // Clear all selections on every zoom change (zoom in/out or within zoom)
        dispatch(clearSelectedRanges());
        dispatch(clearSelectedCards());
        // Keep first card selected for dynamometer
        const firstCardDate = cardDateItems.length > 0 ? [cardDateItems[0].date] : [];
        fetchCardDataFormMultipleSelectedDates(firstCardDate);
      }

      // Detect zoom out to full range (for syncing purposes)
      if (isCurrentlyZoomedIn && !isZoomedNow && !syncChart._isReceivingSync) {
        console.log('Contextualizer zoom out detected - syncing with other charts!');

        // Set the flag to allow this chart to sync
        syncChart._allowZoomSync = true;

        // First, call the regular syncAxes function
        if (syncAxes) {
          console.log('Calling syncAxes from zoom out detection');
          syncAxes(chart);
        }
      }

      // Update previous values for next check
      prevStart = currentStart;
      prevEnd = currentEnd;
      isCurrentlyZoomedIn = isZoomedNow;
    };

    // Check zoom state every 50ms (responsive but not too frequent)
    const zoomMonitorInterval = setInterval(monitorZoomState, 50);

    // Store the interval ID for cleanup
    syncChart._zoomMonitorInterval = zoomMonitorInterval as unknown as number;

    // Add chart reference for syncing - allows this chart to receive zoom updates from other charts
    if (addAmChartRef && typeof chartIndex === 'number') {
      // Mark this chart as a "receiver only" to prevent it from being used as sync source by default
      (
        chart as typeof chart & {
          _isReceiverOnly: boolean;
          _isDragging: boolean;
          _recentDragOperation: boolean;
          _allowZoomSync: boolean;
          _isReceivingSync: boolean;
        }
      )._isReceiverOnly = true;
      (
        chart as typeof chart & {
          _isReceiverOnly: boolean;
          _isDragging: boolean;
          _recentDragOperation: boolean;
          _allowZoomSync: boolean;
          _isReceivingSync: boolean;
        }
      )._isDragging = false;
      isDraggingRef.current = false;
      (
        chart as typeof chart & {
          _isReceiverOnly: boolean;
          _isDragging: boolean;
          _recentDragOperation: boolean;
          _allowZoomSync: boolean;
          _isReceivingSync: boolean;
        }
      )._recentDragOperation = false;
      (
        chart as typeof chart & {
          _isReceiverOnly: boolean;
          _isDragging: boolean;
          _recentDragOperation: boolean;
          _allowZoomSync: boolean;
          _isReceivingSync: boolean;
        }
      )._allowZoomSync = false;
      (
        chart as typeof chart & {
          _isReceiverOnly: boolean;
          _isDragging: boolean;
          _recentDragOperation: boolean;
          _allowZoomSync: boolean;
          _isReceivingSync: boolean;
        }
      )._isReceivingSync = false;
      addAmChartRef(chartIndex, chart);
    }

    return () => {
      // Clear the zoom monitor interval
      if (syncChart._zoomMonitorInterval) {
        clearInterval(syncChart._zoomMonitorInterval);
      }
      // Help GC by clearing refs to disposed chart/axis instances
      chartInstanceRef.current = null;
      xAxisRef.current = null;
      if (rootRef.current) {
        try {
          rootRef.current.dispose();
        } catch {
          // ignore dispose errors
        }
        rootRef.current = null;
      }
      // Cleanup listeners
      window.removeEventListener('resize', handleResize);
      const vv = (window as unknown as { visualViewport?: VisualViewport }).visualViewport;
      if (vv && typeof vv.removeEventListener === 'function') {
        vv.removeEventListener('resize', onVisualViewportResize);
      }
      if (resizeObserver) {
        try {
          resizeObserver.disconnect();
        } catch {
          // ignore observer disconnect errors
        }
        resizeObserver = null;
      }
      clearTimeout(resizeTimeout);
    };
    //}, [chartData, startDate, endDate, selectedRanges]);
  }, [chartData, startDate, endDate]);

  useEffect(() => {
    const controller = new AbortController();
    loadSvgAsDataUri(closeSVG, controller.signal)
      .then(setIconUri)
      .catch((err) => {
        if (err && err.name !== 'AbortError') {
          // swallow non-critical icon load errors
        }
      });
    return () => controller.abort();
  }, []);
  // Separate effect for handling selectedRanges changes and resize completion
  useEffect(() => {
    const chart = chartInstanceRef.current;
    const xAxis = xAxisRef.current;

    if (!chart || !xAxis || !chart.plotContainer || !chart.plotContainer.width()) {
      return;
    }

    type SyncableChart = am5xy.XYChart & {
      _selectionRects?: Set<am5.Rectangle>;
    };
    const syncChart = chart as unknown as SyncableChart;

    // Clear existing selection rectangles thoroughly - use custom property for reliable identification
    const rectanglesToRemove: am5.Rectangle[] = [];
    const closeIconsToRemove: am5.Sprite[] = [];
    const rectContainersToRemove: am5.Sprite[] = [];
    chart.plotContainer.children.each((child: am5.Sprite) => {
      if (child && child instanceof am5.Rectangle && syncChart._selectionRects?.has(child)) {
        rectanglesToRemove.push(child);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } else if (child && child instanceof am5.Picture && (child as any).isCloseIcon) {
        closeIconsToRemove.push(child);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } else if (child && child instanceof am5.Container && (child as any).isRectContainer) {
        rectContainersToRemove.push(child);
      }
    });
    rectanglesToRemove.forEach((rect) => rect.dispose());
    closeIconsToRemove.forEach((icon) => icon.dispose());
    rectContainersToRemove.forEach((container) => container.dispose());
    syncChart._selectionRects?.clear();

    // Add new selection rectangles
    if (selectedRanges.length > 0) {
      const start = xAxis.get('start') ?? 0;
      const end = xAxis.get('end') ?? 1;
      const axisSpan = end - start;
      const safeSpan = axisSpan !== 0 ? axisSpan : 1;
      selectedRanges.forEach(({ minDate, maxDate }) => {
        const minPosition = xAxis.valueToPosition(minDate);
        const maxPosition = xAxis.valueToPosition(maxDate);
        const xMin = ((minPosition - start) / safeSpan) * chart.plotContainer.width();
        const xMax = ((maxPosition - start) / safeSpan) * chart.plotContainer.width();
        const width = Math.max(0, xMax - xMin);
        const x = xMin;
        if (!isNaN(minPosition) && !isNaN(maxPosition)) {
          const rectContainer = chart.plotContainer.children.push(
            am5.Container.new(chart.root, {
              width: width,
              height: am5.percent(100),
              interactive: true,
              x: x,
              layer: 0,
            }),
          );
          const selectionRect = am5.Rectangle.new(chart.root, {
            fill: am5.color('#C3E7F1'),
            fillOpacity: 0.5,
            width: width,
            height: am5.percent(100),
          });
          rectContainer.children.push(selectionRect);

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (rectContainer as any).isRectContainer = true;
          //rectContainer.children.push(selectionRect);
          const closeIcon = rectContainer.children.push(
            am5.Picture.new(chart.root, {
              src: iconUri,
              width: 20,
              height: 20,
              interactive: true,
              x: am5.p100,
              y: 0,
              centerX: am5.p100,
              centerY: 0,
              dx: -4,
              dy: 4,
              cursorOverStyle: 'pointer',
              layer: 1,
            }),
          );
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (closeIcon as any).isCloseIcon = true;

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          closeIcon.events.on('click', (ev: any) => {
            ev.originalEvent.stopPropagation();
            const newRanges = selectedRanges.filter(
              (range) => !(range.minDate === minDate && range.maxDate === maxDate),
            );

            const removeCards = cardDateItems
              .filter((dataPoint) => {
                const dataPointTime = new Date(dataPoint.date).getTime();
                return (
                  dataPointTime >= minDate &&
                  dataPointTime <= maxDate &&
                  !newRanges.some((range) => dataPointTime >= range.minDate && dataPointTime <= range.maxDate)
                );
              })
              ?.map((dataPoint) => dataPoint.date);

            const updatedSelectedCards = selectedCards.filter((date) => !removeCards.includes(date));

            dispatch(setSelectedRanges(newRanges));
            dispatch(removeSelectedCards(removeCards));
            fetchCardDataFormMultipleSelectedDates(updatedSelectedCards);
          });

          closeIcon.set('opacity', 0);

          rectContainer.events.on('pointerover', () => {
            if (isDraggingRef.current) {
              return;
            }
            closeIcon.set('opacity', 1);
          });
          rectContainer.events.on('pointerout', () => {
            closeIcon.set('opacity', 0);
          });
          syncChart._selectionRects?.add(selectionRect);
        }
      });
    }
  }, [selectedRanges, resizeVersion]);

  // Ensure any remaining chart root is disposed on unmount
  useEffect(() => {
    return () => {
      if (rootRef.current) {
        try {
          rootRef.current.dispose();
        } catch {
          // ignore dispose errors
        }
        rootRef.current = null;
      }
    };
  }, []);

  // Trim global selections to the current time range to prevent retaining stale state
  useEffect(() => {
    if (!startDate || !endDate) return;

    const startMs = new Date(startDate).getTime();
    const endMs = new Date(endDate).getTime();

    // Filter ranges that intersect with the current global range
    const filteredRanges = selectedRanges.filter((r) => r.maxDate >= startMs && r.minDate <= endMs);

    // Filter selected card dates to the current global range
    const filteredCardDates = (selectedCards || []).filter((dateStr) => {
      const t = new Date(dateStr).getTime();
      return t >= startMs && t <= endMs;
    });

    const rangesChanged = filteredRanges.length !== selectedRanges.length;
    const cardsChanged = filteredCardDates.length !== (selectedCards || []).length;

    if (rangesChanged || cardsChanged) {
      dispatch(setSelectedRanges(filteredRanges));
      dispatch(updateSelectedCards(filteredCardDates));
      // Refresh chart data for trimmed selections
      fetchCardDataFormMultipleSelectedDates(filteredCardDates);
    }
  }, [startDate, endDate]);

  return (
    <section className='card-contextualizer'>
      {isLoadingCards || !isStreamingComplete ? (
        <Loader />
      ) : chartData?.length === 0 ? (
        <div className='contextualizer-content'>
          <div className='chart-title-section'>
            <h1 className='chart-title'>{chartTitle}</h1>
          </div>
          <div className='chart-section'>
            <div className='no-data-message'>No matching records found, Adjust the time selection</div>
          </div>
        </div>
      ) : (
        <div className='contextualizer-content'>
          <div className='chart-title-section'>
            <h1 className='chart-title'>{chartTitle}</h1>
          </div>
          <div className='chart-section'>
            <div ref={chartRef} className={isCardAnalytics ? 'analytics-amcharts-container' : 'amcharts-container'} />
          </div>
          <div className={isCardAnalytics ? 'legends-section legends-section-analytics' : 'legends-section'}>
            <ContextualizerLegends legendsData={legendsData} />
          </div>
        </div>
      )}
    </section>
  );
};

export default Contextualizer;
